import type {
  ActionReference,
  InputConfig,
  OutputDescription,
  RecordListInputConfig,
  ValueConfig,
  ConditionFieldDescription,
} from "@smartsuite/automation-engine-api";
import {
  RecordListInputConfig_Reference,
  Timestamp,
} from "@smartsuite/automation-engine-api";
import { recordListConfigFrom } from "@smartsuite/automation-conditions";
import type { ContentItem, Doc } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { format } from "date-fns";
import { t } from "i18next";
import * as R from "remeda";
import { valueFunctionToValueDataV } from "../components/PhraseBuilder/helper";
import type { RecordsSourceOption } from "../containers/AutomationForm/Forms/InputForm/RecordsSourceEditor/utils";
import {
  isSupportedOutput,
  memoizedConvertOutputToDynamicSectionOption,
} from "../containers/AutomationForm/Forms/InputForm/utils";
import type { ActionCandidate, TriggerCandidate } from "../types/candidate";
import { isOptionExists } from "./check-option-exists";
import { convertDateToBrowserTimezone } from "./date";
import { usePillTooltip } from "./make-tooltip.hook";
import { findMatchedAction } from "./match-action";
import { rootStore } from "../stores";

// the pills this app builds carry two attributes smartdoc does not model:
// `isError`, which call sites read to mark the editor invalid, and `type`,
// which the loop pills use.
type AutomationPillAttrs = PillNodeAttrs & {
  isError?: boolean;
  type?: string;
};
import { solutionMemberScope } from "./member-scope";
import { actionSolutionId } from "./action-solutions";
import type { DynamicSectionOption } from "../types/line";
import { CURRENT_ITEM_OUTPUT_ID } from "./constants";

interface ConvertersFunctions {
  valueConfigToTargetPills: (
    valueConfig: ValueConfig,
    trigger: TriggerCandidate,
    actions: ActionCandidate[],
    timezone: string,
    label: (label: string) => string,
    inputConfig?: InputConfig,
    actionReference?: ActionReference,
    currentAction?: ActionCandidate,
    loopOptions?: DynamicSectionOption[],
  ) => PillNodeAttrs[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();
  const rootLooping = rootStore.automations.candidate.looping;

  const valueConfigToTargetPills = (
    valueConfig: ValueConfig,
    trigger: TriggerCandidate,
    actions: ActionCandidate[],
    timezone: string,
    label: (label: string) => string = (label) => label,
    inputConfig?: InputConfig,
    actionReference?: ActionReference,
    currentAction?: ActionCandidate,
    loopOptions?: DynamicSectionOption[],
  ): AutomationPillAttrs[] => {
    const looping = currentAction
      ? (rootLooping?.scopeForAction?.(currentAction.hash) ?? rootLooping)
      : rootLooping;
    return valueConfig.values.flatMap((value): AutomationPillAttrs[] => {
      if (value.value.oneofKind === "option") {
        if (inputConfig !== undefined) {
          const isExists = isOptionExists(
            { actions, trigger },
            inputConfig.inputId,
            value.value.option.value,
            actionReference,
            true,
          );
          if (!isExists) {
            return [
              {
                icon: "",
                title: label(value.value.option.label),
                value: JSON.stringify({
                  value: value.value.option.value,
                  target: "option",
                }),
                isError: true,
              },
            ];
          }
        }
        return [
          {
            icon: "",
            title: label(value.value.option.label),
            value: JSON.stringify({
              value: value.value.option.value,
              target: "option",
            }),
          },
        ];
      } else if (value.value.oneofKind === "tag") {
        return [
          {
            icon: "",
            title: value.value.tag.label,
            value: JSON.stringify({
              value: value.value.tag.id,
              target: "tag",
            }),
          },
        ];
      } else if (value.value.oneofKind === "date") {
        const showDate = convertDateToBrowserTimezone(
          Timestamp.toDate(value.value.date),
          timezone,
        );
        return [
          {
            icon: "",
            title: format(showDate, "PP"),
            value: JSON.stringify({
              value: showDate,
              valueUTC: Timestamp.toDate(value.value.date),
              target: "date",
            }),
          },
        ];
      } else if (value.value.oneofKind === "function") {
        if (value.value.function.function.oneofKind === "now") {
          const offset = value.value.function.function.now.offset;
          const dataToTranslate = valueFunctionToValueDataV(
            value.value.function,
          );
          const title: string =
            typeof dataToTranslate === "string"
              ? dataToTranslate
              : t(dataToTranslate.key, { ...dataToTranslate.data });
          return [
            {
              icon: "date-select",
              title,
              value: JSON.stringify({
                value: offset,
                target: "date_function",
              }),
            },
          ];
        }
        if (value.value.function.function.oneofKind === "nowWithTime") {
          const offset = value.value.function.function.nowWithTime.offset;
          const dataToTranslate = valueFunctionToValueDataV(
            value.value.function,
          );
          const title: string =
            typeof dataToTranslate === "string"
              ? dataToTranslate
              : t(dataToTranslate.key, { ...dataToTranslate.data });
          return [
            {
              icon: "date-select",
              title,
              value: JSON.stringify({
                value: offset,
                target: "date_with_time_function",
              }),
            },
          ];
        }
        if (value.value.function.function.oneofKind === "dateAddOffset") {
          const offset = value.value.function.function.dateAddOffset.offset;
          const startOfDay =
            value.value.function.function.dateAddOffset.startOfDay;
          if (value.value.function.function.dateAddOffset.value === undefined) {
            return [];
          }
          return valueConfigToTargetPills(
            value.value.function.function.dateAddOffset.value,
            trigger,
            actions,
            timezone,
          ).map((pill: PillNodeAttrs) => {
            const value = JSON.parse(pill.value);
            return {
              ...pill,
              value: JSON.stringify({
                ...value,
                offset,
                originalTarget: value.target,
                target: "date_offset_function",
                startOfDay,
              }),
            };
          });
        }
      } else if (value.value.oneofKind === "reference") {
        const outputId = value.value.reference.outputId;

        if (outputId === CURRENT_ITEM_OUTPUT_ID) {
          return [
            {
              type: "loop",
              value: JSON.stringify(value.value.reference),
              icon: "",
              title: t("general.current-item"),
              tooltip: makeCurrentItemPillTooltip(looping),
            },
          ];
        }
      }
      if (
        value.value.oneofKind === "reference" &&
        value.value.reference.source.oneofKind === "triggerReference"
      ) {
        const outputId = value.value.reference.outputId;

        let output = getOutputDescriptions(trigger).find((output) => {
          if (value.value.oneofKind === "reference") {
            return (
              output.outputId === value.value.reference.outputId &&
              value.value.reference.source.oneofKind === "triggerReference" &&
              value.value.reference.source.triggerReference.triggerId ===
                trigger.triggerReference?.triggerId
            );
          }
          return false;
        });

        if (!output && loopOptions?.length) {
          output = loopOptions?.find((option) => option.outputId === outputId);
        }

        return [
          {
            icon: output?.icon ?? "",
            title: getLabel(output),
            value: JSON.stringify({
              value: value.value.reference.outputId,
              target: "trigger",
            }),
            isError: output?.label === undefined,
            tooltip: makeTriggerPillTooltip(trigger),
          },
        ];
      } else if (
        value.value.oneofKind === "matchingReference" &&
        value.value.matchingReference.source.oneofKind === "triggerReference"
      ) {
        const output = getOutputDescriptions(trigger).find((output) => {
          if (value.value.oneofKind === "matchingReference") {
            return (
              output.outputId === value.value.matchingReference.outputId &&
              value.value.matchingReference.source.oneofKind ===
                "triggerReference" &&
              value.value.matchingReference.source.triggerReference
                .triggerId === trigger.triggerReference?.triggerId
            );
          }
          return false;
        });
        return [
          {
            icon: output?.icon ?? "",
            title: getLabel(output),
            value: JSON.stringify({
              value: value.value.matchingReference.outputId,
              target: "trigger",
            }),
            isError: output?.label === undefined,
            tooltip: makeTriggerPillTooltip(trigger),
          },
        ];
      } else if (
        value.value.oneofKind === "reference" &&
        value.value.reference.source.oneofKind === "actionReference"
      ) {
        const outputId = value.value.reference.outputId;
        const action = findMatchedAction(value.value.reference.source, actions);
        if (action === undefined) {
          return [];
        }
        const hash = actions.find(
          (item) =>
            value.value.oneofKind === "reference" &&
            value.value.reference.source.oneofKind === "actionReference" &&
            item?.actionReference !== undefined &&
            compareActionReferences(
              item.actionReference,
              value.value.reference.source.actionReference,
            ),
        )?.hash;

        if (
          value.value.reference.outputId.startsWith("currentItem|") ||
          value.value.reference.outputId.startsWith("currentItem.")
        ) {
          const outputOptions = memoizedConvertOutputToDynamicSectionOption(
            looping,
            actions,
            trigger,
          );

          const outputOption = outputOptions?.options.find(
            (option) => option.outputId === outputId,
          );

          return [
            {
              title:
                outputOption?.label ??
                String(t("automation-form.deleted-field-title")),
              icon: outputOption?.icon ?? "",
              value: JSON.stringify({
                target: "action",
                value: value.value.reference.outputId,
                hash,
                isError:
                  outputOption?.label === undefined ||
                  !isSupportedOutput(action, actions, currentAction, looping),
              }),
              tooltip: makeActionPillTooltip(action),
            } as const,
          ];
        }

        const output = getOutputDescriptions(action).find((output) => {
          return (
            value.value.oneofKind === "reference" &&
            output.outputId === value.value.reference.outputId
          );
        });

        return [
          {
            icon: output?.icon ?? "",
            title: getLabel(output),
            value: JSON.stringify({
              value: value.value.reference.outputId,
              target: "action",
              hash,
              isError:
                output?.label === undefined ||
                !isSupportedOutput(action, actions, currentAction, looping),
            }),
            tooltip: makeActionPillTooltip(action),
          },
        ];
      } else if (value.value.oneofKind === "file") {
        return [
          {
            icon: "",
            title: value.value.file?.metadata?.filename ?? "",
            value: JSON.stringify({
              target: "file",
              value: value.value.file,
            }),
          },
        ];
      } else if (
        value.value.oneofKind === "reference" &&
        value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID
      ) {
        return [
          {
            type: "loop",
            value: JSON.stringify(value.value.reference),
            icon: "",
            title: t("general.current-item"),
          },
        ];
      }

      return [];
    });
  };

  return {
    valueConfigToTargetPills,
  };
};

// TODO: make it reusable
// use it ONLY in StringEditor
export const valueConfigToSmartDoc = (
  inputConfig: ValueConfig,
  trigger: TriggerCandidate,
  actions: ActionCandidate[],
  currentAction?: ActionCandidate,
  loopOptions?: DynamicSectionOption[],
): {
  doc: Doc;
  hasInvalidPills: boolean;
} => {
  let hasInvalidPills = false;
  const rootLooping = rootStore.automations.candidate.looping;
  const looping = currentAction
    ? (rootLooping?.scopeForAction?.(currentAction.hash) ?? rootLooping)
    : rootLooping;
  const content: ContentItem[] = inputConfig.values.flatMap((value) => {
    if (value?.value === undefined) return [];

    if (value.value.oneofKind === "string" && value.value.string === "\n") {
      return { type: "hard_break" };
    } else if (value.value.oneofKind === "string") {
      const result = toSmartDocText(value.value.string);
      return result === undefined ? [] : [result];
    } else if (
      value.value.oneofKind === "function" &&
      value.value.function.function.oneofKind === "teamId"
    ) {
      const teamId = value.value.function.function.teamId.teamId;
      // A team pill is only resolvable if the team has access to the solution
      // this action writes into -- same scope the picker offered.
      const team = solutionMemberScope(
        rootStore,
        actionSolutionId(currentAction, rootStore.config.solutionId),
      ).teams.find((item) => item.teamId === teamId);

      return [
        {
          type: "pill",
          attrs: {
            icon: "",
            title: team?.name ?? "Unknown Team",
            value: JSON.stringify({
              target: "team",
              teamId,
            }),
            invalid: team === undefined,
          },
        },
      ];
    } else if (
      value.value.oneofKind === "reference" &&
      value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID
    ) {
      return [
        {
          type: "pill",
          attrs: {
            icon: "",
            title: String(t("general.current-item")),
            value: JSON.stringify(value.value.reference),
          },
        },
      ];
    } else if (
      value.value.oneofKind === "reference" &&
      value.value.reference.source.oneofKind === "triggerReference"
    ) {
      const outputId = value.value.reference.outputId;
      let output = getOutputDescriptions(trigger).find((output) => {
        return (
          value.value.oneofKind === "reference" &&
          output.outputId === value.value.reference.outputId &&
          value.value.reference.source.oneofKind === "triggerReference" &&
          value.value.reference.source.triggerReference.triggerId ===
            trigger.triggerReference?.triggerId
        );
      });

      if (output === undefined && loopOptions?.length) {
        output = loopOptions.find((option) => option.outputId === outputId);
      }

      if (output === undefined) {
        hasInvalidPills = true;
      }

      return [
        {
          type: "pill",
          attrs: {
            icon: output?.icon ?? "",
            title: getLabel(output),
            value: JSON.stringify(value.value.reference),
            invalid: output?.label === undefined,
          },
        } as const,
      ];
    } else if (
      value.value.oneofKind === "reference" &&
      value.value.reference.source.oneofKind === "actionReference"
    ) {
      const outputId = value.value.reference.outputId;
      const action = actions.find(
        (action) =>
          value.value.oneofKind === "reference" &&
          value.value.reference.source.oneofKind === "actionReference" &&
          action.actionReference?.actionId ===
            value.value.reference.source.actionReference.actionId &&
          action.actionReference?.instanceId ===
            value.value.reference.source.actionReference.instanceId,
      );
      if (action === undefined) {
        return [];
      }

      const output = getOutputDescriptions(action).find((output) => {
        return (
          value.value.oneofKind === "reference" && output.outputId === outputId
        );
      });

      if (!isSupportedOutput(action, actions, currentAction, looping)) {
        return [
          {
            type: "pill",
            attrs: {
              icon: output?.icon ?? "",
              title:
                output?.label ??
                String(t("automation-form.deleted-field-title")),
              value: JSON.stringify(value.value.reference),
              invalid: true,
            },
          },
        ];
      }

      if (value.value.reference.outputId.startsWith("currentItem|")) {
        const outputOptions = memoizedConvertOutputToDynamicSectionOption(
          looping,
          actions,
          trigger,
        );

        const outputOption = outputOptions?.options.find(
          (option) => option.outputId === outputId,
        );

        return [
          {
            type: "pill",
            attrs: {
              icon: outputOption?.icon ?? "",
              title:
                outputOption?.label ??
                String(t("automation-form.deleted-field-title")),
              value: JSON.stringify(value.value.reference),
              invalid: outputOption?.label === undefined,
            },
          } as const,
        ];
      }

      if (output === undefined) {
        hasInvalidPills = true;
      }

      return [
        {
          type: "pill",
          attrs: {
            icon: output?.icon ?? "",
            title: getLabel(output),
            value: JSON.stringify(value.value.reference),
            invalid: output?.label === undefined,
          },
        } as const,
      ];
    }
    return [];
  });

  return {
    doc: {
      type: "doc",
      content: [
        {
          type: "paragraph",
          attrs: {
            textAlign: null,
          },
          content:
            content.length > 0 ? content : R.compact([toSmartDocText("")]),
        },
      ],
    },
    hasInvalidPills,
  };
};

const toSmartDocText = (text: string): ContentItem | undefined => {
  if (text.length === 0) {
    return undefined;
  } else {
    return {
      type: "text",
      text,
    };
  }
};

export const recordsSourceOptionToRecordListInputConfig = (
  option: RecordsSourceOption,
): RecordListInputConfig | undefined => {
  switch (option.type) {
    case "action":
      if (option.actionReference === undefined) {
        return undefined;
      }
      return {
        recordList: {
          oneofKind: "actionReference",
          actionReference: option.actionReference,
        },
        ...recordListConfigFrom(option.recordListOutput),
      };
    case "trigger":
      return {
        recordList: {
          oneofKind: "reference",
          reference: RecordListInputConfig_Reference.FROM_TRIGGER,
        },
        ...recordListConfigFrom(option.recordListOutput),
      };
    default:
      return undefined;
  }
};

export const recordListInputConfigToOptionValue = (
  recordList?: RecordListInputConfig,
): string | undefined => {
  if (recordList === undefined) {
    return undefined;
  }
  switch (recordList.recordList.oneofKind) {
    case "actionReference":
      return `${recordList.recordList.actionReference.actionId}_${recordList.recordList.actionReference.instanceId}`;
    case "reference":
      return recordList.recordList.reference ===
        RecordListInputConfig_Reference.FROM_TRIGGER
        ? "trigger"
        : undefined;
    default:
      return undefined;
  }
};

export const getOutputDescriptions = (
  entity: TriggerCandidate | ActionCandidate,
  useInConditions = false,
): OutputDescription[] => {
  const outputs =
    entity.description.recordListOutput !== undefined
      ? [
          ...entity.description.recordListOutput.fields,
          ...entity.description.outputs,
        ]
      : entity.description.outputs;

  return !useInConditions
    ? outputs
    : [
        ...outputs,
        ...entity.description.conditionFields.map(toOutputDescription),
      ];
};

const toOutputDescription = (
  field: ConditionFieldDescription,
): OutputDescription => ({
  outputId: field.conditionFieldId,
  icon: field.icon,
  label: field.label,
  value: field.value,
  contextSpecific: true,
});

export const compareActionReferences = (
  left: ActionReference,
  right: ActionReference,
): boolean =>
  left.actionId === right.actionId &&
  left.integrationId === right.integrationId &&
  left.instanceId === right.instanceId;

export const getLabel = (field?: OutputDescription): string => {
  if (!field?.label) {
    return String(t("automation-form.deleted-field-title"));
  }
  const labelText = field.labelSuffix
    ? `${field.label}: ${field.labelSuffix}`
    : field.label;
  return labelText;
};
